/**
 * Reading and writing the system-managed AUM custom attribute of an investment or of its pending
 * proposal — the whole mechanism, from identifying the attribute to building the save request's
 * `customAttributes`.
 *
 * The surfaces that persist an AUM with a composition — the portfolio lab's "Update composition"
 * commit and the quantity-mode composition editor — carry it inside the entity-editor save request's
 * `customAttributes`, so it applies atomically with the composition; there is no separate AUM
 * endpoint. The portfolio Main Info step edits it through `./AumField` and saves it with the step's
 * own `customAttributes`. Deliberately one file: the pieces below are only ever used together, by
 * each other, so splitting them just spreads one mechanism over several imports.
 */
import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	type CustomAttributeValue,
	InvestmentsEnhancementStaticConfigurationControllerApiFactory,
	InvestmentsStaticConfigurationControllerApiFactory,
	type SaveEditorEditEntityEntityEnum,
} from "@eu.mdotm.api/sphere";
import { investmentCustomAttributesRepository } from "./repository";
import type { CustomAttributeDto } from "./type";

// Same repository namespace as `./repository`, so the AUM read shares its query-key prefix and its
// invalidations. Declared here rather than in that file because this module reads the cached
// attribute list FROM it — hosting the query there would close an import cycle (`npm run
// analyze-cycles`).
const { newQuery } = repositoryHelperFactory("customAttributes");

const staticConfigApi = () => getApiGen(InvestmentsStaticConfigurationControllerApiFactory);
const enhancedStaticConfigApi = () => getApiGen(InvestmentsEnhancementStaticConfigurationControllerApiFactory);

/** The `systemKey` of the backend-managed AUM custom attribute — fixed, identical for every customer. */
export const AUM_SYSTEM_KEY = "AUM";

/** Whether `attribute` is the system-managed AUM attribute (`systemKey === "AUM"`). */
export function isAumSystemAttribute(attribute: CustomAttributeDto): boolean {
	return attribute.systemKey === AUM_SYSTEM_KEY;
}

/** Finds the system-managed AUM attribute (`systemKey === "AUM"`), or `undefined` when the tenant has none. */
export function findAumSystemAttribute(attributes: CustomAttributeDto[] | undefined): CustomAttributeDto | undefined {
	return attributes?.find(isAumSystemAttribute);
}

/**
 * Returns `values` with `value` upserted by `attributeUuid`: an existing entry keeps its other
 * fields and gets the new ones merged in; a missing entry is appended. The editor save request's
 * `customAttributes` has replace-all semantics, so the caller must always send the FULL current
 * value set with the mutated entry — never the mutated entry alone.
 */
export function upsertCustomAttributeValue(
	values: CustomAttributeValue[],
	value: CustomAttributeValue,
): CustomAttributeValue[] {
	const exists = values.some((v) => v.attributeUuid === value.attributeUuid);
	if (!exists) {
		return [...values, value];
	}
	return values.map((v) => (v.attributeUuid === value.attributeUuid ? { ...v, ...value } : v));
}

/**
 * Which state an AUM read/write targets — the entity-editor entities that can carry
 * `customAttributes`. The attribute values live on a DIFFERENT configuration per state, so every
 * caller must say which one it means, and must pass the SAME entity it saves to: the current
 * investment and a draft keep their values in the static configuration, a pending proposal in the enhanced
 * static configuration (the split the portfolio-settings editor makes between its
 * `settings-current` and `settings-enhanced` areas). Derived via `Extract` so a rename in the API
 * enum surfaces here as a type error.
 */
export type AumCommitTarget = Extract<
	SaveEditorEditEntityEntityEnum,
	"INVESTMENT" | "INVESTMENT_ENHANCEMENT" | "INVESTMENT_DRAFT"
>;

/**
 * The custom-attribute values `target` currently carries, read fresh (never cached) from the
 * configuration that owns them — the base a replace-all `customAttributes` write must build on.
 */
async function readCustomAttributeValues(
	investmentUuid: string,
	target: AumCommitTarget,
): Promise<CustomAttributeValue[]> {
	const configuration = await axiosExtract(
		target === "INVESTMENT_ENHANCEMENT"
			? enhancedStaticConfigApi().getStaticEnhancementConfiguration(investmentUuid)
			: staticConfigApi().getStaticConfiguration(investmentUuid),
	);
	return configuration.mainInfo?.customAttributes ?? [];
}

/**
 * Builds the value entry that assigns `aum` to the tenant's system-managed AUM attribute, ready to
 * be upserted into a save request's `customAttributes`.
 *
 * `null` when the tenant exposes no AUM attribute (`systemKey === "AUM"`) or it carries no
 * `attributeUuid` — callers treat that as "skip the AUM write", which is also what happens while
 * the backend has not shipped the attribute. Callers own the decision of WHETHER there is an AUM
 * worth writing; this only turns one into an attribute value.
 */
export function buildAumAttributeValue(
	attributes: CustomAttributeDto[] | undefined,
	aum: number,
): CustomAttributeValue | null {
	const aumAttribute = findAumSystemAttribute(attributes);
	if (aumAttribute?.attributeUuid == null) {
		return null;
	}
	return { attributeUuid: aumAttribute.attributeUuid, numberValue: aum };
}

/**
 * Resolves the `customAttributes` array a save on `target` must carry to also persist `aum`, or
 * `undefined` for a composition-only save (field omitted — the backend leaves the attributes
 * untouched).
 *
 * `undefined` when there is no AUM to persist (`null`/non-positive) or while the tenant exposes no
 * AUM attribute. The save request's `customAttributes` has replace-all semantics, so `target`'s
 * CURRENT values are read back (see {@link AumCommitTarget}) and the AUM entry is upserted into
 * them — never sent alone. Any failure here throws BEFORE the save request is sent, failing the
 * whole save atomically: nothing changes on the server.
 */
export async function resolveCustomAttributesWithAum(
	investmentUuid: string,
	aum: number | null,
	target: AumCommitTarget,
): Promise<CustomAttributeValue[] | undefined> {
	if (aum == null || aum <= 0) {
		return undefined;
	}
	const attributes = await investmentCustomAttributesRepository.customAttributes.fetchQuery();
	const aumValue = buildAumAttributeValue(attributes, aum);
	if (!aumValue) {
		return undefined;
	}
	const currentValues = await readCustomAttributeValues(investmentUuid, target);
	return upsertCustomAttributeValue(currentValues, aumValue);
}

/**
 * The AUM `target` currently carries on the system-managed AUM attribute — `null` when the tenant
 * has no AUM attribute or the entity carries no value for it. Cached: read it through
 * {@link investmentAum}.
 */
async function readAumAttributeValue(investmentUuid: string, target: AumCommitTarget): Promise<number | null> {
	const [attributes, currentValues] = await Promise.all([
		investmentCustomAttributesRepository.customAttributes.fetchQuery(),
		readCustomAttributeValues(investmentUuid, target),
	]);
	const aumAttribute = findAumSystemAttribute(attributes);
	if (aumAttribute?.attributeUuid == null) {
		return null;
	}
	return currentValues.find((value) => value.attributeUuid === aumAttribute.attributeUuid)?.numberValue ?? null;
}

/**
 * Cached read of the AUM an investment (or its pending proposal) carries on the system-managed AUM
 * attribute — `null` when the tenant has no AUM attribute or the entity has no value for it.
 */
export const investmentAum = newQuery(
	"investmentAum",
	(investmentUuid: string, target: AumCommitTarget): Promise<number | null> =>
		readAumAttributeValue(investmentUuid, target),
);
